import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, DollarSign, Beef, Flame, Radio, Search, X, CheckCircle } from 'lucide-react'
import {
  createIncome, getIncomeTypes, getPaymentMethods,
  getBiocharBatches, lookupByRfid, getOwnerSegments, getCashAccounts
} from '../../services/api'
import RfidScanModal from '../../components/RfidScanModal'
import useUnsavedChanges from '../../hooks/useUnsavedChanges'
import { useAuth } from '../../contexts/AuthContext'

export default function IncomeForm({ ranchId }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { userId } = useAuth()

  // Check if we came from a cattle sale event or biochar batch
  const prefilledData = location.state || {}

  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    income_type: prefilledData.income_type || 'cattle_sale',
    description: prefilledData.description || '',
    amount: prefilledData.amount || '',
    income_date: prefilledData.income_date || new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    payer_name: prefilledData.payer_name || '',
    payer_contact: prefilledData.payer_contact || '',
    linked_animal_id: prefilledData.linked_animal_id || '',
    linked_biochar_batch_id: prefilledData.linked_biochar_batch_id || '',
    reference_number: '',
    sale_weight_kg: prefilledData.sale_weight_kg || '',
    reported_by: userId,
    received_by_owner_segment_id: '', // who actually got the money (105)
    received_into_account_id: '', // mig 118: farm account, if not owner-direct
  })
  const [error, setError] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  useUnsavedChanges(isDirty)

  // RFID scan modal state
  const [scanModalOpen, setScanModalOpen] = useState(false)
  const [selectedAnimal, setSelectedAnimal] = useState(prefilledData.selectedAnimal || null)
  const [manualRfid, setManualRfid] = useState('')
  const [rfidLooking, setRfidLooking] = useState(false)
  const [rfidError, setRfidError] = useState('')

  // Fetch reference data
  const { data: incomeTypes } = useQuery({
    queryKey: ['incomeTypes'],
    queryFn: getIncomeTypes,
  })

  const { data: paymentMethods } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: getPaymentMethods,
  })

  // Owner parties for sale-proceeds attribution (105). Defaults (server-side)
  // to the animal's owner; override only when a different owner banked it.
  const { data: cashAccounts } = useQuery({
    queryKey: ['cashAccounts', ranchId],
    queryFn: () => getCashAccounts(ranchId),
    enabled: !!ranchId,
  })

  const { data: ownerSegments } = useQuery({
    queryKey: ['ownerSegments', ranchId],
    queryFn: () => getOwnerSegments(ranchId),
    enabled: !!ranchId,
  })
  const multiOwner = (ownerSegments?.length || 0) > 1

  // Fetch biochar batches for linking
  const { data: biocharData } = useQuery({
    queryKey: ['biocharBatches', ranchId],
    queryFn: () => getBiocharBatches(ranchId, { limit: 100 }),
    enabled: formData.income_type === 'biochar',
  })

  const biocharBatches = biocharData?.items || []

  // Handle animal found from RFID scan modal
  const handleAnimalFound = (animal) => {
    setScanModalOpen(false)
    setSelectedAnimal(animal)
    setFormData(prev => ({ ...prev, linked_animal_id: animal.id }))
    setRfidError('')
  }

  // Handle manual RFID lookup
  const handleManualRfidLookup = async () => {
    const tag = manualRfid.trim()
    if (!tag) return
    setRfidLooking(true)
    setRfidError('')
    try {
      const response = await lookupByRfid(tag, ranchId)
      if (!response.found || !response.animal) {
        setRfidError(`No animal found with RFID "${tag}"`)
        return
      }
      if (response.animal.status === 'sold') {
        setRfidError(`"${response.animal.local_name || tag}" has already been sold`)
        return
      }
      setSelectedAnimal(response.animal)
      setFormData(prev => ({ ...prev, linked_animal_id: response.animal.id }))
      setManualRfid('')
    } catch (err) {
      setRfidError(err.response?.data?.detail || 'Lookup failed')
    } finally {
      setRfidLooking(false)
    }
  }

  // Clear selected animal
  const clearSelectedAnimal = () => {
    setSelectedAnimal(null)
    setFormData(prev => ({ ...prev, linked_animal_id: '', description: '' }))
  }

  // Create income mutation
  const createMutation = useMutation({
    mutationFn: createIncome,
    onSuccess: () => {
      queryClient.invalidateQueries(['income'])
      queryClient.invalidateQueries(['financeSummary'])
      navigate('/finance')
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record income'),
  })

  // Auto-fill description based on linked item
  useEffect(() => {
    if (selectedAnimal && !formData.description) {
      setFormData(prev => ({
        ...prev,
        description: `Sale of ${selectedAnimal.category}: ${selectedAnimal.local_name || selectedAnimal.rfid_tag}`
      }))
    }
  }, [selectedAnimal])

  useEffect(() => {
    if (formData.linked_biochar_batch_id && !formData.description) {
      const batch = biocharBatches.find(b => b.id === formData.linked_biochar_batch_id)
      if (batch) {
        setFormData(prev => ({
          ...prev,
          description: `Biochar sale: Batch ${batch.batch_code} (${batch.output_weight_kg}kg)`
        }))
      }
    }
  }, [formData.linked_biochar_batch_id])

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    // Validate cattle_sale requires animal selection and sale weight
    if (formData.income_type === 'cattle_sale') {
      if (!formData.linked_animal_id) {
        setError('Please select the animal being sold')
        return
      }
      if (!formData.sale_weight_kg) {
        setError('Please enter the sale weight')
        return
      }
    }

    // Build notes from contact info and reference number
    const notesParts = [
      formData.payer_contact ? `Buyer contact: ${formData.payer_contact}` : null,
      formData.reference_number ? `Reference: ${formData.reference_number}` : null,
    ].filter(Boolean)

    const payload = {
      ranch_id: formData.ranch_id,
      income_type: formData.income_type,
      description: formData.description,
      amount: parseFloat(formData.amount),
      income_date: formData.income_date,
      payment_method: formData.payment_method,
      buyer_name: formData.payer_name || null,
      linked_animal_ids: formData.linked_animal_id ? [formData.linked_animal_id] : [],
      linked_batch_id: formData.linked_biochar_batch_id || null,
      notes: notesParts.length > 0 ? notesParts.join('. ') : null,
      reported_by: formData.reported_by,
      sale_weight_kg: formData.sale_weight_kg ? parseFloat(formData.sale_weight_kg) : null,
      payer_msisdn: formData.payer_msisdn || null,
      received_by_owner_segment_id: formData.received_by_owner_segment_id || null,
      received_into_account_id: formData.received_into_account_id || null,
    }

    createMutation.mutate(payload)
  }

  const getIncomeTypeIcon = (type) => {
    switch (type) {
      case 'cattle_sale':
        return <Beef className="h-5 w-5" />
      case 'biochar':
        return <Flame className="h-5 w-5" />
      default:
        return <DollarSign className="h-5 w-5" />
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Record Income</h1>
          <p className="text-gray-600">Track farm income and sales</p>
        </div>
      </div>

      {/* RFID Scan Modal */}
      <RfidScanModal
        isOpen={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        onAnimalFound={handleAnimalFound}
        mode="lookup"
        ranchId={ranchId}
      />

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} onChange={() => setIsDirty(true)} className="space-y-6">
        {/* Income Type Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Income Type</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(incomeTypes || [
              { value: 'cattle_sale', label: 'Cattle Sale' },
              { value: 'milk', label: 'Milk Sales' },
              { value: 'biochar', label: 'Biochar Sales' },
              { value: 'breeding_service', label: 'Breeding Service' },
              { value: 'other', label: 'Other' },
            ]).map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  setFormData({
                    ...formData,
                    income_type: type.value,
                    linked_animal_id: '',
                    linked_biochar_batch_id: '',
                    description: ''
                  })
                  setSelectedAnimal(null)
                }}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                  formData.income_type === type.value
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {getIncomeTypeIcon(type.value)}
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Link to Cattle Sale */}
        {formData.income_type === 'cattle_sale' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Animal Sale Details</h2>

            <div className="space-y-4">
              {/* Selected Animal Display */}
              {selectedAnimal ? (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {selectedAnimal.local_name || selectedAnimal.rfid_tag}
                    </div>
                    <div className="text-sm text-gray-600">
                      {selectedAnimal.rfid_tag}
                      {selectedAnimal.category && ` \u2022 ${selectedAnimal.category}`}
                      {selectedAnimal.status && ` \u2022 ${selectedAnimal.status}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedAnimal}
                    className="p-1 text-gray-400 hover:text-red-500 rounded"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Identify Animal Sold *
                  </label>

                  {/* Scan RFID Button */}
                  <button
                    type="button"
                    onClick={() => setScanModalOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium mb-3"
                  >
                    <Radio className="h-5 w-5" />
                    Scan RFID Tag
                  </button>

                  {/* Manual RFID Entry */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center" aria-hidden>
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-xs text-gray-500">or enter RFID manually</span>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={manualRfid}
                        onChange={(e) => { setManualRfid(e.target.value); setRfidError('') }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleManualRfidLookup() } }}
                        placeholder="Enter RFID tag (e.g. UG-2026-0100)"
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleManualRfidLookup}
                      disabled={!manualRfid.trim() || rfidLooking}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {rfidLooking ? 'Looking...' : 'Look Up'}
                    </button>
                  </div>

                  {rfidError && (
                    <p className="mt-2 text-sm text-red-600">{rfidError}</p>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    The animal's status will be updated to "sold" and a sale event will be recorded
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sale Weight (kg) *
                </label>
                <input
                  type="number"
                  required
                  step="0.1"
                  min="0"
                  value={formData.sale_weight_kg}
                  onChange={(e) => setFormData({ ...formData, sale_weight_kg: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Weight at time of sale"
                />
              </div>
            </div>
          </div>
        )}

        {/* Link to Biochar Batch */}
        {formData.income_type === 'biochar' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Link to Biochar Batch</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Batch Sold
              </label>
              <select
                value={formData.linked_biochar_batch_id}
                onChange={(e) => setFormData({ ...formData, linked_biochar_batch_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">-- Optional: Link to batch --</option>
                {biocharBatches.map(batch => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batch_code} - {batch.output_weight_kg}kg ({batch.status})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Linking helps track biochar sales for carbon credit verification
              </p>
            </div>
          </div>
        )}

        {/* Amount & Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (UGX) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="100"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.income_date}
                onChange={(e) => setFormData({ ...formData, income_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method *
              </label>
              <select
                required
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                {paymentMethods?.map(method => (
                  <option key={method.value} value={method.value}>{method.label}</option>
                )) || (
                  <>
                    <option value="cash">Cash</option>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank">Bank Transfer</option>
                  </>
                )}
              </select>
            </div>

            {multiOwner && formData.income_type === 'cattle_sale' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Received by (which owner)
                </label>
                <select
                  value={formData.received_by_owner_segment_id}
                  onChange={(e) => { setFormData({ ...formData, received_by_owner_segment_id: e.target.value }); setIsDirty(true) }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Defaults to the animal&apos;s owner</option>
                  {ownerSegments?.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Only set this if a different owner banked the money (e.g. a common-herd animal) — it&apos;s flagged for reconciliation in Director Accounts.
                </p>
              </div>
            )}

            {(cashAccounts?.items || []).length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Where did the money land?
                </label>
                <select
                  value={formData.received_into_account_id}
                  onChange={(e) => { setFormData({ ...formData, received_into_account_id: e.target.value }); setIsDirty(true) }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Owner&apos;s personal account (the usual)</option>
                  {cashAccounts.items.map(a => (
                    <option key={a.id} value={a.id}>Farm account: {a.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Pick a farm account only if the buyer paid into the farm float —
                  the Monday balance check then expects that movement.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference Number
              </label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Transaction ID / Receipt #"
              />
            </div>

            {formData.payment_method === 'mobile_money' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payer Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.payer_msisdn || ''}
                  onChange={(e) => setFormData({ ...formData, payer_msisdn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="256XXXXXXXXX"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <input
                type="text"
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="What is this income for?"
              />
            </div>
          </div>
        </div>

        {/* Payer Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Payer Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payer Name
              </label>
              <input
                type="text"
                value={formData.payer_name}
                onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Name of buyer/payer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payer Contact
              </label>
              <input
                type="tel"
                value={formData.payer_contact}
                onChange={(e) => setFormData({ ...formData, payer_contact: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="+256..."
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {createMutation.isPending ? 'Saving...' : 'Record Income'}
          </button>
        </div>
      </form>
    </div>
  )
}
